
import { 
    collection, 
    doc, 
    setDoc, 
    getDoc, 
    getDocs, 
    updateDoc, 
    addDoc, 
    query, 
    where,
    orderBy,
    serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../src/firebaseConfig';
import { ClientProfile, UserProfile, SubmissionLog, DraftApplication, CommunicationLog, FeedbackLog } from '../types';

/**
 * DB SERVICE
 * Handles all interactions with Firestore and Storage.
 */

export const getPublicIP = async (): Promise<string> => {
    try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        return data.ip;
    } catch (e) {
        console.warn("Could not fetch IP address for audit log.", e);
        return 'unknown';
    }
};

// --- USER OPERATIONS ---

/**
 * Syncs the user profile and returns true if this is a brand new user
 */
export const syncUserProfile = async (uid: string, email: string): Promise<boolean> => {
    if (!db) return false;
    
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    let isNewUser = false;

    if (!userSnap.exists()) {
        isNewUser = true;
        const newUser: UserProfile = {
            uid,
            email,
            role: 'user',
            settings: {
                multiClientMode: false,
                emailNotifications: true
            },
            consents: {
                termsAcceptedAt: new Date().toISOString(),
                privacyPolicyAcceptedAt: new Date().toISOString()
            },
            createdAt: new Date().toISOString(),
            lastLoginAt: new Date().toISOString()
        };
        await setDoc(userRef, newUser);
    } else {
        await updateDoc(userRef, {
            lastLoginAt: new Date().toISOString()
        });
    }
    return isNewUser;
};

// --- CLIENT OPERATIONS ---

export const saveClientProfile = async (userId: string, clientData: ClientProfile) => {
    if (!db) return;

    const clientRef = doc(db, 'users', userId, 'clients', clientData.clientId);
    
    const payload = {
        ...clientData,
        ownerId: userId,
        updatedAt: new Date().toISOString()
    };

    await setDoc(clientRef, payload, { merge: true });
};

export const getClientsForUser = async (userId: string): Promise<ClientProfile[]> => {
    if (!db) return [];

    const clientsRef = collection(db, 'users', userId, 'clients');
    const querySnapshot = await getDocs(clientsRef);
    const clients: ClientProfile[] = [];
    
    querySnapshot.forEach((doc) => {
        const data = doc.data() as ClientProfile;
        if (!data.isArchived) {
            clients.push(data);
        }
    });

    return clients;
};

// --- AUTO-SAVE & DRAFTS ---

export const uploadSecureDocument = async (userId: string, clientId: string, file: File, docType: string): Promise<string> => {
    if (!storage) throw new Error("Storage not configured");

    const fileRef = ref(storage, `users/${userId}/clients/${clientId}/uploads/${docType}_${Date.now()}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
};

export const saveDraft = async (userId: string, clientId: string, draftData: Partial<DraftApplication>) => {
    if (!db) return;
    const clientRef = doc(db, 'users', userId, 'clients', clientId);
    const updates: Record<string, any> = { 'draft.lastUpdated': new Date().toISOString() };
    if (draftData.step) updates['draft.step'] = draftData.step;
    if (draftData.formData) updates['draft.formData'] = draftData.formData;
    if (draftData.uploadedFiles) updates['draft.uploadedFiles'] = draftData.uploadedFiles;
    await updateDoc(clientRef, updates);
};

// --- GLOBAL LOGGING (AUDIT & FEEDBACK) ---

export const logSubmission = async (userId: string, log: SubmissionLog) => {
    if (!db) return;
    await addDoc(collection(db, 'submissions'), {
        ...log,
        userId: userId,
        loggedAt: serverTimestamp()
    });
};

export const logCommunication = async (userId: string, clientId: string, log: CommunicationLog) => {
    if (!db) return;
    const commRef = collection(db, 'users', userId, 'clients', clientId, 'communications');
    await addDoc(commRef, log);
};

export const logFeedback = async (feedback: FeedbackLog) => {
    if (!db) return;
    await addDoc(collection(db, 'feedback'), {
        ...feedback,
        loggedAt: serverTimestamp()
    });
};

export const getFeedbackLogs = async (): Promise<FeedbackLog[]> => {
    if (!db) return [];
    const feedbackRef = collection(db, 'feedback');
    const q = query(feedbackRef, orderBy('timestamp', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FeedbackLog));
};
